import { phishingSignsLabels } from '@/client/campaigns';
import { CampaignUserResultStatusesEnum } from '@/client/campaigns/types/campaign.type';
import { MyPhishingSimulationDetail } from '@/client/my-phishing';
import CampaignThumbnailPlaceholder from '@/images/placeholders/campaign-thumbnail-placeholder.svg';
import AppChip from '@/ui/chip/AppChip';
import { FormatDate } from '@/ui/date';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const StyledTealBanner = styled.div`
  background-color: var(--teal-deep);
  border-radius: 20px;
  padding: 30px;
  display: flex;
  align-items: center;
  gap: 10px;

  @media screen and (max-width: 860px) {
    flex-direction: column;
    align-items: flex-start;
    padding: var(--default-padding);
  }
`;

const StyledBannerInnerRow = styled.div`
  display: flex;
  align-items: center;
  gap: 40px;
  width: 100%;

  @media screen and (max-width: 860px) {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--default-padding);
  }
`;

const StyledBannerThumbnail = styled.div`
  width: 115px;
  height: 71px;
  border-radius: var(--box-border-radius);
  overflow: hidden;
  flex-shrink: 0;
  background-color: var(--white-main);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media screen and (max-width: 480px) {
    width: 80px;
    height: 50px;
  }
`;

const StyledBannerInfoRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 15px;
  flex: 1;
  min-width: 0;

  @media screen and (max-width: 860px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const StyledBannerTextCol = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 6px;
  flex: 1;
  min-width: 0;
`;

const StyledBannerTitle = styled.h1`
  margin: 0;
  font-weight: 600;
  font-size: 22px;
  line-height: 33px;
  color: var(--white-main);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media screen and (max-width: 860px) {
    font-size: var(--semi-big-font-size);
    line-height: var(--semi-big-line-height);
    white-space: normal;
  }
`;

const StyledBannerDate = styled.span`
  font-weight: 500;
  font-size: var(--xsmall-font-size);
  line-height: var(--xsmall-line-height);
  color: var(--white-main);
`;

const StyledBannerPills = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--semi-xsmall-padding);
  flex-wrap: wrap;

  .p-chip {
    font-size: var(--xsmall-font-size);
    font-weight: 500;
  }
`;

const StyledBannerSignPill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 9px;
  border-radius: 100px;
  background-color: var(--white-main);
  color: var(--green-color);
  font-weight: 500;
  font-size: var(--xsmall-font-size);
  line-height: var(--xsmall-line-height);
  white-space: nowrap;
`;

export const getStatusChipType = (
  status: CampaignUserResultStatusesEnum,
): string => {
  switch (status) {
    case CampaignUserResultStatusesEnum.CLICKED_LINK:
    case CampaignUserResultStatusesEnum.SUBMITTED_DATA:
      return 'error';
    case CampaignUserResultStatusesEnum.EMAIL_REPORTED:
      return 'primary';
    default:
      return 'secondary';
  }
};

export const getStatusTranslationKey = (
  status: CampaignUserResultStatusesEnum,
): string => {
  switch (status) {
    case CampaignUserResultStatusesEnum.CLICKED_LINK:
      return 'phishing.followUp.status.clickedLink';
    case CampaignUserResultStatusesEnum.SUBMITTED_DATA:
      return 'phishing.followUp.status.submittedData';
    case CampaignUserResultStatusesEnum.EMAIL_REPORTED:
      return 'phishing.followUp.status.emailReported';
    case CampaignUserResultStatusesEnum.EMAIL_SENT:
      return 'phishing.followUp.status.emailSent';
    default:
      return 'phishing.followUp.status.noInteraction';
  }
};

type SimulationDetailBannerProps = {
  detail: MyPhishingSimulationDetail;
};

export const SimulationDetailBanner: React.FC<SimulationDetailBannerProps> = ({
  detail,
}) => {
  const { t } = useTranslation();
  const labels = phishingSignsLabels(t);
  const phishingSignLabel = detail.phishingSign
    ? labels[detail.phishingSign as keyof typeof labels]
    : undefined;

  return (
    <StyledTealBanner>
      <StyledBannerInnerRow>
        <StyledBannerThumbnail>
          <img
            src={detail.thumbnailUrl ?? CampaignThumbnailPlaceholder}
            alt=""
          />
        </StyledBannerThumbnail>
        <StyledBannerInfoRow>
          <StyledBannerTextCol>
            <StyledBannerTitle>{detail.name}</StyledBannerTitle>
            <StyledBannerDate>
              {t('phishing.followUp.detail.received')}{' '}
              <FormatDate date={detail.receivedAt} format="D MMM YYYY" />
            </StyledBannerDate>
          </StyledBannerTextCol>
          <StyledBannerPills>
            {phishingSignLabel && (
              <StyledBannerSignPill>{phishingSignLabel}</StyledBannerSignPill>
            )}
            <AppChip
              label={t(getStatusTranslationKey(detail.learnerResultStatus))}
              type={getStatusChipType(detail.learnerResultStatus)}
              size="xs-auto"
            />
          </StyledBannerPills>
        </StyledBannerInfoRow>
      </StyledBannerInnerRow>
    </StyledTealBanner>
  );
};
